import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, FilePlus2, FileUp, Loader2, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import letterService from '../../services/letterService';
import cvService from '../../services/cvService';
import { trackEvent } from '../../utils/analytics';
import {
    CITIES_CI,
    EMPTY_ANSWERS,
    EXPERIENCE_LEVELS,
    GAP_REASONS,
    LetterAnswers,
    QUALITIES,
    SECTORS,
} from '../../types/letter';

const TOTAL_STEPS = 6;

const ChoiceButton: React.FC<{
    selected?: boolean;
    onClick: () => void;
    children: React.ReactNode;
    className?: string;
}> = ({ selected, onClick, children, className = '' }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-5 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
            selected
                ? 'border-teal-600 bg-teal-50 text-teal-900 shadow-sm'
                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
        } ${className}`}
    >
        {children}
    </button>
);

const LetterWizard: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, logout } = useAuth();
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState<LetterAnswers>({ ...EMPTY_ANSWERS });
    const [letterId, setLetterId] = useState<string | null>(searchParams.get('id'));
    const [cvs, setCvs] = useState<any[]>([]);
    const [selectedCvId, setSelectedCvId] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!user?._id) return;
        cvService.getUserCVs(user._id).then(setCvs).catch(() => setCvs([]));
    }, [user?._id]);

    useEffect(() => {
        if (!letterId) return;
        letterService
            .get(letterId)
            .then((l) => {
                if (l.answers) setAnswers({ ...EMPTY_ANSWERS, ...l.answers });
                if (typeof l.wizardStep === 'number') setStep(l.wizardStep);
            })
            .catch(() => {});
    }, [letterId]);

    const patch = useCallback((partial: Partial<LetterAnswers>) => {
        setAnswers((prev) => ({ ...prev, ...partial }));
    }, []);

    const persist = async (nextStep: number, extra: Partial<LetterAnswers> = {}) => {
        setSaving(true);
        setError('');
        const merged = { ...answers, ...extra };
        try {
            if (!letterId) {
                const created = await letterService.create({
                    answers: merged,
                    wizardStep: nextStep,
                    title: `Lettre — ${merged.firstName || user?.fullName || 'brouillon'}`,
                    sourceCvId: selectedCvId || null,
                });
                setLetterId(created._id!);
                setAnswers(merged);
                setStep(nextStep);
                trackEvent('letter_step_completed', { step: nextStep });
                navigate(`/creer-lettre?id=${created._id}`, { replace: true });
            } else {
                await letterService.update(letterId, {
                    answers: merged,
                    wizardStep: nextStep,
                    sourceCvId: selectedCvId || null,
                });
                setAnswers(merged);
                setStep(nextStep);
                trackEvent('letter_step_completed', { step: nextStep });
            }
        } catch (e: any) {
            setError(e?.response?.data?.error || e.message || 'Erreur de sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const goNext = async () => {
        if (step === 0) {
            if (answers.creationMode === 'from-cv' && selectedCvId) {
                const cv = cvs.find((c) => c._id === selectedCvId);
                if (cv?.personalInfo) {
                    const pi = cv.personalInfo;
                    const lastExp = cv.experience?.[0];
                    await persist(1, {
                        firstName: pi.firstName || '',
                        lastName: pi.lastName || '',
                        email: pi.email || '',
                        phone: pi.phone || '',
                        address: pi.address || pi.city || '',
                        lastJobTitle: lastExp?.position || pi.profession || '',
                        fieldOfStudy: cv.education?.[0]?.field || '',
                        degree: cv.education?.[0]?.degree || '',
                        hasDegree: !!(cv.education?.[0]?.degree),
                    });
                    return;
                }
            }
            await persist(1);
            return;
        }
        if (step === TOTAL_STEPS - 1) {
            setSaving(true);
            try {
                const id =
                    letterId ||
                    (
                        await letterService.create({
                            answers,
                            wizardStep: step,
                            title: `Lettre — ${answers.firstName || 'brouillon'}`,
                        })
                    )._id!;
                if (!letterId) setLetterId(id);
                await letterService.update(id, { answers, wizardStep: step });
                navigate(`/choix-modele-lettre?id=${id}`);
            } catch (e: any) {
                setError(e?.response?.data?.error || e.message || 'Erreur');
            } finally {
                setSaving(false);
            }
            return;
        }
        await persist(step + 1);
    };

    const goBack = () => {
        if (step === 0) {
            navigate('/espace');
            return;
        }
        setStep((s) => s - 1);
    };

    const canContinue = () => {
        if (step === 0) {
            if (answers.creationMode === 'from-cv') return !!selectedCvId;
            return true;
        }
        if (step === 1) return !!answers.experienceLevel && !!answers.sector;
        if (step === 2) {
            if (!answers.hasTargetJob) return true;
            return !!answers.jobTitle;
        }
        if (step === 3) return answers.qualities.length === 3;
        if (step === 4) {
            if (!answers.hasGap) return true;
            return !!answers.gapReason;
        }
        if (step === 5) return !!(answers.firstName && answers.lastName && answers.additionalContext.trim());
        return true;
    };

    const toggleQuality = (q: string) => {
        setAnswers((prev) => {
            const has = prev.qualities.includes(q);
            if (has) return { ...prev, qualities: prev.qualities.filter((x) => x !== q) };
            if (prev.qualities.length >= 3) return prev;
            return { ...prev, qualities: [...prev.qualities, q] };
        });
    };

    const phases = ['Création', 'Parcours', 'Poste', 'Qualités', 'Inactivité', 'Identité'];

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <header className="border-b border-slate-100 px-6 py-4 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => navigate('/espace')}
                    className="text-xl font-bold tracking-tight text-slate-900"
                >
                    Flash<span className="text-teal-600">CV</span>
                </button>
                <div className="hidden sm:flex items-center gap-6 text-xs font-medium text-slate-500">
                    {phases.map((p, i) => (
                        <div key={p} className="flex items-center gap-2">
                            <span
                                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] ${
                                    i < step
                                        ? 'bg-teal-600 text-white'
                                        : i === step
                                          ? 'border-2 border-teal-600 text-teal-700'
                                          : 'border border-slate-300 text-slate-400'
                                }`}
                            >
                                {i < step ? '✓' : i + 1}
                            </span>
                            <span className={i === step ? 'text-slate-800' : ''}>{p}</span>
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={logout}
                    className="text-slate-500 hover:text-slate-800 p-2"
                    title="Déconnexion"
                >
                    <LogOut className="w-5 h-5" />
                </button>
            </header>

            <main className="max-w-3xl w-full mx-auto px-6 pt-12 pb-6">
                {error && (
                    <div className="mb-6 rounded-lg bg-red-50 text-red-700 text-sm px-4 py-3">{error}</div>
                )}

                {step === 0 && (
                    <div className="space-y-8">
                        <div className="text-center">
                            <h1 className="text-3xl font-serif font-bold text-slate-900">
                                Comment souhaitez-vous créer votre lettre de motivation ?
                            </h1>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <button
                                type="button"
                                onClick={() => patch({ creationMode: 'new' })}
                                className={`p-8 rounded-xl border-2 text-left transition ${
                                    answers.creationMode === 'new'
                                        ? 'border-teal-600 border-dashed bg-teal-50/50'
                                        : 'border-slate-200 hover:border-slate-300'
                                }`}
                            >
                                <FilePlus2 className="w-8 h-8 text-teal-700 mb-3" />
                                <h2 className="font-bold text-slate-900">Créer une nouvelle lettre</h2>
                                <p className="text-sm text-slate-500 mt-1">Nous commencerons par le début</p>
                            </button>
                            <button
                                type="button"
                                onClick={() => patch({ creationMode: 'from-cv' })}
                                className={`p-8 rounded-xl border-2 text-left transition ${
                                    answers.creationMode === 'from-cv'
                                        ? 'border-teal-600 border-dashed bg-teal-50/50'
                                        : 'border-slate-200 hover:border-slate-300'
                                }`}
                            >
                                <FileUp className="w-8 h-8 text-teal-700 mb-3" />
                                <h2 className="font-bold text-slate-900">Utiliser mon CV</h2>
                                <p className="text-sm text-slate-500 mt-1">
                                    Nous importerons les informations de votre CV
                                </p>
                            </button>
                        </div>
                        {answers.creationMode === 'from-cv' && (
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                                    Sélectionnez un CV
                                </label>
                                {cvs.length === 0 ? (
                                    <p className="text-sm text-slate-500">
                                        Aucun CV trouvé.{' '}
                                        <button
                                            type="button"
                                            className="text-teal-700 underline"
                                            onClick={() => navigate('/choix-modele')}
                                        >
                                            Créer un CV
                                        </button>
                                    </p>
                                ) : (
                                    <select
                                        value={selectedCvId}
                                        onChange={(e) => setSelectedCvId(e.target.value)}
                                        className="w-full border border-slate-300 rounded-lg px-4 py-3"
                                    >
                                        <option value="">— Choisir —</option>
                                        {cvs.map((cv) => (
                                            <option key={cv._id} value={cv._id}>
                                                {cv.title || cv.template}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {step === 1 && (
                    <div className="space-y-10">
                        <div className="text-center space-y-2">
                            <h1 className="text-3xl font-serif font-bold text-slate-900">
                                Depuis combien de temps travaillez-vous ?
                            </h1>
                            <p className="text-slate-500">
                                Nous adapterons le ton et le contenu à votre niveau d’expérience.
                            </p>
                        </div>
                        <div className="flex flex-wrap justify-center gap-3">
                            {EXPERIENCE_LEVELS.map((lvl) => (
                                <ChoiceButton
                                    key={lvl}
                                    selected={answers.experienceLevel === lvl}
                                    onClick={() => patch({ experienceLevel: lvl })}
                                >
                                    {lvl}
                                </ChoiceButton>
                            ))}
                        </div>
                        <div className="space-y-4">
                            <h2 className="text-xl font-serif font-bold text-center">
                                Vos études sont-elles en cours actuellement ?
                            </h2>
                            <div className="flex justify-center gap-3">
                                <ChoiceButton
                                    selected={answers.studying === true}
                                    onClick={() => patch({ studying: true })}
                                >
                                    Oui
                                </ChoiceButton>
                                <ChoiceButton
                                    selected={answers.studying === false}
                                    onClick={() => patch({ studying: false })}
                                >
                                    Non
                                </ChoiceButton>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <h2 className="text-xl font-serif font-bold text-center">
                                Avez-vous obtenu un diplôme universitaire ?
                            </h2>
                            <div className="flex justify-center gap-3">
                                <ChoiceButton
                                    selected={answers.hasDegree === true}
                                    onClick={() => patch({ hasDegree: true })}
                                >
                                    Oui
                                </ChoiceButton>
                                <ChoiceButton
                                    selected={answers.hasDegree === false}
                                    onClick={() => patch({ hasDegree: false, degree: '', fieldOfStudy: '' })}
                                >
                                    Non
                                </ChoiceButton>
                            </div>
                            {answers.hasDegree && (
                                <div className="grid sm:grid-cols-2 gap-4 max-w-xl mx-auto">
                                    <div>
                                        <label className="text-xs font-bold uppercase text-slate-500">
                                            Diplôme
                                        </label>
                                        <input
                                            className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                                            value={answers.degree}
                                            onChange={(e) => patch({ degree: e.target.value })}
                                            placeholder="Licence, Master…"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold uppercase text-slate-500">
                                            Domaine d’études
                                        </label>
                                        <input
                                            className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                                            value={answers.fieldOfStudy}
                                            onChange={(e) => patch({ fieldOfStudy: e.target.value })}
                                            placeholder="Comptabilité, Informatique…"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="space-y-3">
                            <h2 className="text-xl font-serif font-bold text-center">
                                Dans quel secteur évoluez-vous ?
                            </h2>
                            <div className="flex flex-wrap justify-center gap-2">
                                {SECTORS.map((s) => (
                                    <ChoiceButton
                                        key={s}
                                        selected={answers.sector === s}
                                        onClick={() => patch({ sector: s })}
                                    >
                                        {s}
                                    </ChoiceButton>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-8">
                        <div className="text-center space-y-2">
                            <h1 className="text-3xl font-serif font-bold text-slate-900">
                                Avez-vous un poste spécifique en tête ?
                            </h1>
                            <p className="text-slate-500">
                                Notre IA rédigera une lettre que vous pourrez personnaliser ensuite.
                            </p>
                        </div>
                        <div className="flex justify-center gap-3">
                            <ChoiceButton
                                selected={answers.hasTargetJob === true}
                                onClick={() => patch({ hasTargetJob: true })}
                            >
                                Oui
                            </ChoiceButton>
                            <ChoiceButton
                                selected={answers.hasTargetJob === false}
                                onClick={() =>
                                    patch({ hasTargetJob: false, jobTitle: '', company: '', city: '' })
                                }
                            >
                                Pas encore
                            </ChoiceButton>
                        </div>
                        {answers.hasTargetJob && (
                            <div className="space-y-4 max-w-lg mx-auto">
                                <div>
                                    <label className="text-xs font-bold uppercase text-slate-500">
                                        Intitulé du poste
                                    </label>
                                    <input
                                        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                                        value={answers.jobTitle}
                                        onChange={(e) => patch({ jobTitle: e.target.value })}
                                        placeholder="Ex. Chargé de clientèle"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase text-slate-500">
                                        Entreprise (optionnel)
                                    </label>
                                    <input
                                        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                                        value={answers.company}
                                        onChange={(e) => patch({ company: e.target.value })}
                                        placeholder="Ex. Orange CI, SGBCI…"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase text-slate-500">Ville</label>
                                    <select
                                        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                                        value={answers.city}
                                        onChange={(e) => patch({ city: e.target.value })}
                                    >
                                        <option value="">—</option>
                                        {CITIES_CI.map((c) => (
                                            <option key={c} value={c}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                        <div className="max-w-lg mx-auto">
                            <label className="text-xs font-bold uppercase text-slate-500">
                                Intitulé de votre dernier poste
                            </label>
                            <input
                                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                                value={answers.lastJobTitle}
                                onChange={(e) => patch({ lastJobTitle: e.target.value })}
                                placeholder="Ex. Représentant du service clientèle"
                            />
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-6">
                        <div className="text-center space-y-2">
                            <h1 className="text-3xl font-serif font-bold text-slate-900">
                                Choisissez vos trois principales qualités
                            </h1>
                            <p className="text-slate-500">
                                Montrer ce qui vous rend unique vous permet de vous démarquer.
                            </p>
                        </div>
                        <div className="flex flex-wrap justify-center gap-2">
                            {QUALITIES.map((q) => {
                                const selected = answers.qualities.includes(q);
                                return (
                                    <button
                                        key={q}
                                        type="button"
                                        onClick={() => toggleQuality(q)}
                                        className={`px-4 py-2 rounded-full text-sm border transition ${
                                            selected
                                                ? 'bg-teal-600 text-white border-teal-600'
                                                : 'bg-white border-slate-200 text-slate-700 hover:border-teal-400'
                                        }`}
                                    >
                                        {selected ? '✓ ' : '+ '}
                                        {q}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-center text-sm text-slate-400">
                            {answers.qualities.length}/3 sélectionnées
                        </p>
                    </div>
                )}

                {step === 4 && (
                    <div className="space-y-8">
                        <div className="text-center space-y-2">
                            <h1 className="text-3xl font-serif font-bold text-slate-900">
                                Avez-vous une période d’inactivité à expliquer ?
                            </h1>
                            <p className="text-slate-500">
                                Si vous n’avez pas travaillé pour quelque raison, nous vous aiderons à
                                l’expliquer.
                            </p>
                        </div>
                        <div className="flex justify-center gap-3">
                            <ChoiceButton
                                selected={answers.hasGap === true}
                                onClick={() => patch({ hasGap: true })}
                            >
                                Oui
                            </ChoiceButton>
                            <ChoiceButton
                                selected={answers.hasGap === false}
                                onClick={() => patch({ hasGap: false, gapReason: '' })}
                            >
                                Non
                            </ChoiceButton>
                        </div>
                        {answers.hasGap && (
                            <div className="space-y-3">
                                <h2 className="text-lg font-serif font-bold text-center">
                                    Pourquoi avez-vous arrêté de travailler ?
                                </h2>
                                <div className="flex flex-wrap justify-center gap-2">
                                    {GAP_REASONS.map((r) => (
                                        <ChoiceButton
                                            key={r}
                                            selected={answers.gapReason === r}
                                            onClick={() => patch({ gapReason: r })}
                                        >
                                            {r}
                                        </ChoiceButton>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {step === 5 && (
                    <div className="space-y-8">
                        <div className="text-center space-y-2">
                            <h1 className="text-3xl font-serif font-bold text-slate-900">
                                Vos coordonnées
                            </h1>
                            <p className="text-slate-500">
                                Elles apparaîtront en en-tête de votre lettre.
                            </p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4 max-w-xl mx-auto">
                            {(
                                [
                                    ['firstName', 'Prénom'],
                                    ['lastName', 'Nom'],
                                    ['email', 'E-mail'],
                                    ['phone', 'Téléphone'],
                                ] as const
                            ).map(([key, label]) => (
                                <div key={key}>
                                    <label className="text-xs font-bold uppercase text-slate-500">
                                        {label}
                                    </label>
                                    <input
                                        className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                                        value={answers[key]}
                                        onChange={(e) => patch({ [key]: e.target.value })}
                                    />
                                </div>
                            ))}
                            <div className="sm:col-span-2">
                                <label className="text-xs font-bold uppercase text-slate-500">
                                    Adresse / Ville
                                </label>
                                <input
                                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
                                    value={answers.address}
                                    onChange={(e) => patch({ address: e.target.value })}
                                    placeholder="Cocody, Abidjan"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="text-xs font-bold uppercase text-slate-500">
                                    Contexte de votre candidature
                                </label>
                                <p className="text-xs text-slate-400 mt-0.5 mb-1">
                                    Décrivez l'offre d'emploi visée, votre motivation ou tout élément utile pour orienter l'IA.
                                </p>
                                <textarea
                                    className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 resize-none"
                                    rows={4}
                                    value={answers.additionalContext}
                                    onChange={(e) => patch({ additionalContext: e.target.value })}
                                    placeholder="Ex : Je postule à une offre d'employé de bureau chez Nestlé CI, vue sur LinkedIn. Je souhaite insister sur mon sérieux et ma ponctualité."
                                />
                            </div>
                        </div>
                    </div>
                )}
            </main>

            <footer className="max-w-3xl w-full mx-auto px-6 py-4 flex items-center justify-center gap-4 relative z-[80]">
                <button
                    type="button"
                    onClick={goBack}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                    <ArrowLeft className="w-4 h-4" /> Retour
                </button>
                <button
                    type="button"
                    disabled={!canContinue() || saving}
                    onClick={goNext}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-teal-700 text-white font-medium hover:bg-teal-800 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {step === TOTAL_STEPS - 1 ? 'Choisir un modèle' : 'Continuer'}
                </button>
            </footer>
        </div>
    );
};

export default LetterWizard;
